import { OrganizationFeatureFlag } from '@doss/common-all'
import { useCallback, useSyncExternalStore } from 'react'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import {
  clearConfigbaseNavOverride,
  retrieveConfigbaseNavOverride,
  storeConfigbaseNavOverride,
} from '~/common/utils/local-storage'

// Same-tab subscribers; other tabs arrive through the window `storage` event.
const listeners = new Set<() => void>()

const subscribe = (onChange: () => void) => {
  listeners.add(onChange)
  window.addEventListener('storage', onChange)
  return () => {
    listeners.delete(onChange)
    window.removeEventListener('storage', onChange)
  }
}

/**
 * Whether the configbase nav replaces the project nav.
 * The org's `configbaseNav` flag decides, unless an internal user has set a local override.
 * Setting the override back to the flag's value clears it, so the user follows the flag again.
 */
export const useConfigbaseNavEnabled = () => {
  const { user, isInternal } = useGlobalContext()
  const userId = user?.id
  const isFlagEnabled =
    user?.organizationFeatureFlags[OrganizationFeatureFlag.ConfigbaseNav] === true

  const override = useSyncExternalStore(subscribe, () =>
    isInternal && userId ? retrieveConfigbaseNavOverride(userId) : null,
  )

  const setIsEnabled = useCallback(
    (isEnabled: boolean) => {
      if (!userId) {
        return
      }
      if (isEnabled === isFlagEnabled) {
        clearConfigbaseNavOverride(userId)
      } else {
        storeConfigbaseNavOverride(userId, isEnabled)
      }
      listeners.forEach((listener) => listener())
    },
    [userId, isFlagEnabled],
  )

  return { isEnabled: override ?? isFlagEnabled, setIsEnabled }
}
